import React, { useEffect, useState } from 'react';
import DropdownSearch from '../../../../components/dropdownSearch';
import CustomInput from '../../../../components/customInput';
import CustomButton from '../../../../components/customButton';
import { initializeMapping } from '../../../../utils/integrations';
import { useDispatch, useSelector } from 'react-redux';
import { getIntegrations } from '../../../../redux/integration/actions';
import API from '../../../../services/api';
import { getSelectedProject } from '../../../../redux/projects/actions';
import { setAlert } from '../../../../redux/dashboard/actions';
import utils from '../../../../utils';

const CreateForm = ({ site, onCreate, createMode, updateSite }) => {
  const [displayName, setDisplayName] = useState('');
  const [credentials, setCredentials] = useState([]);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [values, setValues] = useState({});

  const integrations = useSelector(getIntegrations);
  const project = useSelector(getSelectedProject);

  const dispatch = useDispatch();

  useEffect(() => {
    setSelectedIntegration(integrations ? integrations[0] : null);
  }, []);

  useEffect(() => {
    const fetchCredentials = async () => {
      if (selectedIntegration) {
        const filter = encodeURI(`vendor = "${selectedIntegration.vendor}"`);
        const res = await API.getCredentials(project.id, '', filter, 200);
        setCredentials(res.credentials);
        setSelectedCredential(res.credentials ? res.credentials[0] : null);
      }
    };
    if (selectedIntegration) {
      fetchCredentials();
      setValues(initializeMapping(selectedIntegration.mappingMetadataFields));
    }
  }, [selectedIntegration]);

  useEffect(() => {
    if (createMode && isIntegrationValid()) {
      updateSite(prev => ({
        ...prev,
        integration: {
          credentialId: selectedCredential.id,
          vendor: selectedIntegration.vendor,
          metadata: values,
          displayName: displayName ? displayName : ''
        }
      }));
    } else {
      updateSite(prev => ({
        ...prev,
        integration: null
      }));
    }
  }, [selectedCredential, selectedIntegration, values, displayName]);

  const isIntegrationValid = () => {
    const hasIntegration = Boolean(selectedIntegration);
    const hasCredential = Boolean(selectedCredential);
    const valueKeys = Object.keys(values);
    const hasValues = valueKeys.length > 0;
    const areAllValuesFilled =
      hasValues &&
      valueKeys.every(key => {
        const value = values[key];
        return value !== undefined && value !== null && `${value}`.trim() !== '';
      });

    return hasIntegration && hasCredential && areAllValuesFilled;
  };

  const createMapping = () => {
    if (selectedCredential === '' || !site) {
      dispatch(
        setAlert(utils.generateAlert('Could not create Credential! Is something wrong with the form?', 'error'))
      );
      return;
    }
    const requestBody = {
      credentialId: selectedCredential.id,
      siteId: site.id,
      vendor: selectedIntegration.vendor,
      metadata: values,
      displayName: displayName ? displayName : ''
    };

    API.createMapping(project.id, requestBody)
      .then(() => {
        dispatch(setAlert(utils.generateAlert('Mapping created successfuly!', 'success')));
        setValues(initializeMapping(selectedIntegration.mappingMetadataFields));
        onCreate();
      })
      .catch(err => {
        console.error(err);
        dispatch(setAlert(utils.generateAlert('Could not create Mapping! Please try again.', 'error')));
      });
  };

  const updateForm = (field, value) => {
    setValues(prevValues => ({
      ...prevValues,
      [field]: value
    }));
  };

  return (
    <div className="asset-create">
      <div className="account-fields">
        <div className="form-row">
          <div className="col">
            <div className="form-group">
              <label>Integration</label>
              <DropdownSearch
                title={'Search Integration'}
                selected={
                  selectedIntegration
                    ? {
                        label: selectedIntegration.vendor[0] + selectedIntegration.vendor.slice(1).toLowerCase(),
                        value: selectedIntegration.vendor
                      }
                    : { label: 'Select integration', value: '' }
                }
                leftAlign
                onSelect={value => {
                  setSelectedIntegration(integrations.find(integ => integ.vendor === value));
                  setValues(
                    initializeMapping(integrations.find(integ => integ.vendor === value).mappingMetadataFields)
                  );
                }}
                options={
                  integrations
                    ? integrations.map(integration => {
                        return {
                          label: integration.vendor[0] + integration.vendor.slice(1).toLowerCase(),
                          value: integration.vendor
                        };
                      })
                    : []
                }
              />
            </div>
          </div>
          <div className="col" key="credential-id">
            <div className="form-group">
              <label>Credential</label>
              <DropdownSearch
                title={'Search Credential'}
                customEmptyMessage="None found. Create on Integrations Page"
                selected={
                  selectedCredential
                    ? { label: selectedCredential.displayName, value: selectedCredential.id }
                    : { label: 'Select credential', value: '' }
                }
                leftAlign
                onSelect={value => {
                  setSelectedCredential(credentials.find(cred => cred.id === value));
                }}
                options={
                  credentials
                    ? credentials.map(credential => {
                        return { label: credential.displayName, value: credential.id };
                      })
                    : []
                }
              />
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="col" key="asset-name">
            <div className="form-group">
              <label>Name</label>
              <CustomInput
                id="asset-name"
                type="text"
                value={displayName}
                isEditable
                onChange={e => {
                  setDisplayName(e.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <div className="form-row">
          {selectedIntegration &&
            selectedIntegration.mappingMetadataFields.map(field => {
              return (
                <div className="col" key={field}>
                  <div className="form-group">
                    <label>{field}</label>
                    <CustomInput
                      id={`new-mappings-${field}`}
                      type="text"
                      value={values[field]}
                      isEditable
                      onChange={e => {
                        updateForm(field, e.target.value);
                      }}
                    />
                  </div>
                </div>
              );
            })}
        </div>
        {!createMode && (
          <div className="form-row justify-content-end">
            <CustomButton
              classes="btn-danger left-button"
              title="Cancel"
              handleClick={() => {
                setSelectedCredential('');
                setValues(initializeMapping(selectedIntegration.mappingMetadataFields));
              }}
            />
            <CustomButton
              classes="btn-secondary ml-2"
              title="Create"
              handleClick={createMapping}
              disabled={!isIntegrationValid()}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateForm;
